import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { guestsApi } from '../services/api';
import {
  Search,
  Star,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  AlertTriangle,
  X,
  Plus,
  MessageSquare,
  Users,
  TrendingUp,
  Clock,
  Heart,
  ThumbsDown,
  CalendarDays,
  UtensilsCrossed,
  UserCheck,
  Sparkles,
  Loader2,
  PartyPopper,
  XCircle,
  CheckCircle,
  Tag,
  RefreshCw,
  UserCircle,
  BarChart3,
  Settings2,
  Link2,
  BookOpen,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import { format } from 'date-fns';
import clsx from 'clsx';

type GuestTab = 'overview' | 'reservations' | 'orders' | 'servers' | 'insights';

interface GuestTag {
  id: string;
  tag: string;
  layer: 'status' | 'behavior' | 'preference' | 'relationship' | 'risk';
  source: 'auto' | 'manual' | 'ai';
  confidence?: string;
  addedAt: string;
  notes?: string;
}

interface GuestTagsByLayer {
  status: GuestTag[];
  behavior: GuestTag[];
  preference: GuestTag[];
  relationship: GuestTag[];
  risk: GuestTag[];
}

interface TagDefinition {
  name: string;
  description?: string;
  isSystem: boolean;
  color?: string;
}

interface SystemTagDefinitions {
  status: TagDefinition[];
  behavior: TagDefinition[];
  preference: TagDefinition[];
  relationship: TagDefinition[];
  risk: TagDefinition[];
}

interface Guest {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  totalVisits: number;
  lastVisitAt?: string;
  totalSpendCents: number;
  averageCheckSizeCents: number;
  isVIP: boolean;
  allergies?: string[];
  dietaryRestrictions?: string[];
  preferences?: {
    lovedItems?: string[];
    dislikedItems?: string[];
    itemHistory?: Array<{
      menuItemId: string;
      menuItemName: string;
      orderCount: number;
      sentiment: string;
      lastOrderedAt: string;
    }>;
  };
  notes?: string;
  birthday?: string;
  anniversary?: string;
  tags?: string[];
  spendTier?: string;
  createdAt: string;
  recentChecks?: Array<{
    id: string;
    totalCents: number;
    openedAt: string;
    closedAt?: string;
    tableName?: string;
    serverName?: string;
  }>;
  reservationHistory?: Array<{
    id: string;
    date: string;
    time: string;
    partySize: number;
    occasion?: string;
    status: string;
    specialRequests?: string;
    notes?: string;
  }>;
  serverHistory?: Array<{
    serverId: string;
    serverName: string;
    visitCount: number;
    lastServed: string;
    totalSpentCents: number;
    tablesServed?: string;
  }>;
}

interface GuestInsights {
  visitSummary: string;
  preferences: {
    loves: string;
    avoids: string;
  };
  serviceNotes: string;
  recommendations: string[];
  highlights: string[];
  aiPowered: boolean;
}

export default function GuestsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showVipOnly, setShowVipOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [activeTab, setActiveTab] = useState<GuestTab>('overview');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
    allergies: '',
    dietaryRestrictions: '',
    isVIP: false,
  });
  const [formError, setFormError] = useState('');

  // Fetch guests
  const { data: guestsData, isLoading } = useQuery({
    queryKey: ['guests', searchQuery, showVipOnly, tagFilter],
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (searchQuery) params.search = searchQuery;
      if (showVipOnly) params.isVip = true;
      if (tagFilter) params.tag = tagFilter;
      const response = await guestsApi.getAll(params);
      return response.data?.data || response.data || [];
    },
  });

  // Fetch single guest details
  const { data: guestDetails } = useQuery({
    queryKey: ['guest', selectedGuest?.id],
    queryFn: async () => {
      if (!selectedGuest?.id) return null;
      const response = await guestsApi.getById(selectedGuest.id);
      return response.data?.data || response.data;
    },
    enabled: !!selectedGuest?.id,
  });

  // Fetch AI insights only when insights tab is active
  const { data: insightsData, isLoading: insightsLoading } = useQuery({
    queryKey: ['guest-insights', selectedGuest?.id],
    queryFn: async () => {
      if (!selectedGuest?.id) return null;
      const response = await guestsApi.getInsights(selectedGuest.id);
      return response.data?.data || response.data;
    },
    enabled: !!selectedGuest?.id && activeTab === 'insights',
  });

  const insights = insightsData as GuestInsights | null;

  // Fetch guest tags
  const { data: tagsData, isLoading: tagsLoading } = useQuery({
    queryKey: ['guest-tags', selectedGuest?.id],
    queryFn: async () => {
      if (!selectedGuest?.id) return null;
      const response = await guestsApi.getTags(selectedGuest.id);
      return response.data?.data || response.data;
    },
    enabled: !!selectedGuest?.id,
  });

  const guestTags = tagsData as { tagsByLayer: GuestTagsByLayer; summary: Record<string, string[]> } | null;

  // Fetch upcoming celebrations (birthdays & anniversaries)
  const { data: celebrationsData } = useQuery({
    queryKey: ['upcoming-celebrations'],
    queryFn: async () => {
      const response = await guestsApi.getUpcomingCelebrations({ days: 30, type: 'all' });
      return response.data?.data || response.data;
    },
  });

  const upcomingCelebrations = (celebrationsData as any)?.upcoming || [];

  const guests = Array.isArray(guestsData) ? guestsData : [];

  // Create guest mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => guestsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      setShowAddModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: '',
        allergies: '',
        dietaryRestrictions: '',
        isVIP: false,
      });
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create guest');
    },
  });

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => guestsApi.addNote(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest', selectedGuest?.id] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      setNewNote('');
    },
  });

  // Toggle VIP mutation
  const toggleVipMutation = useMutation({
    mutationFn: ({ id, isVIP }: { id: string; isVIP: boolean }) => guestsApi.markVip(id, isVIP),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest', selectedGuest?.id] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });

  // Add tag mutation
  const addTagMutation = useMutation({
    mutationFn: ({ id, tag, layer }: { id: string; tag: string; layer: string }) =>
      guestsApi.addTag(id, { tag, layer }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-tags', selectedGuest?.id] });
      setShowAddTagDropdown(false);
      setNewTagInput('');
    },
  });

  // Remove tag mutation
  const removeTagMutation = useMutation({
    mutationFn: ({ guestId, tagId }: { guestId: string; tagId: string }) =>
      guestsApi.removeTag(guestId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-tags', selectedGuest?.id] });
    },
  });

  // Recompute tags mutation
  const computeTagsMutation = useMutation({
    mutationFn: (id: string) => guestsApi.computeTags(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-tags', selectedGuest?.id] });
    },
  });

  const [showAddTagDropdown, setShowAddTagDropdown] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [selectedTagLayer, setSelectedTagLayer] = useState<string>('preference');
  const [showTagManager, setShowTagManager] = useState(false);
  const [newCustomTag, setNewCustomTag] = useState({ name: '', layer: 'preference', description: '' });

  // Fetch tag definitions (glossary)
  const { data: tagDefinitionsData } = useQuery({
    queryKey: ['tag-definitions'],
    queryFn: async () => {
      const response = await guestsApi.getTagDefinitions();
      return response.data?.data || response.data;
    },
    enabled: showTagManager,
  });

  const tagDefinitions = tagDefinitionsData as { definitions: any[]; systemDefaults: SystemTagDefinitions } | null;

  // Refresh all tags mutation
  const refreshAllTagsMutation = useMutation({
    mutationFn: () => guestsApi.refreshAllTags(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['guest-tags'] });
    },
  });

  // Create custom tag definition
  const createTagDefMutation = useMutation({
    mutationFn: (data: { name: string; layer: string; description?: string }) =>
      guestsApi.createTagDefinition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tag-definitions'] });
      setNewCustomTag({ name: '', layer: 'preference', description: '' });
    },
  });

  // Edit contact info state
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ email: '', phone: '', birthday: '', anniversary: '' });

  // Update guest mutation
  const updateGuestMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      guestsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest', selectedGuest?.id] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-celebrations'] });
      setEditingContact(false);
    },
  });

  // Send celebration reminder state and mutation
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  const [reminderSuccess, setReminderSuccess] = useState<string | null>(null);

  const sendReminderMutation = useMutation({
    mutationFn: (data: { guestId: string; occasion: 'birthday' | 'anniversary' }) =>
      guestsApi.sendCelebrationReminder(data),
    onSuccess: (_, variables) => {
      const celebration = upcomingCelebrations.find(
        (c: any) => c.guestId === variables.guestId && c.eventType === variables.occasion
      );
      setReminderSuccess(`Sent ${variables.occasion} invite to ${celebration?.guestName || 'guest'}`);
      setTimeout(() => setReminderSuccess(null), 3000);
      setSendingReminderId(null);
      // Refresh celebrations to show "Sent" badge
      queryClient.invalidateQueries({ queryKey: ['upcoming-celebrations'] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Failed to send reminder');
      setSendingReminderId(null);
    },
  });

  const resetReminderMutation = useMutation({
    mutationFn: (data: { guestId: string; occasion: 'birthday' | 'anniversary'; celebrationDate: string }) =>
      guestsApi.resetCelebrationReminder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upcoming-celebrations'] });
    },
  });

  // Initialize contact form when guest changes
  const initContactForm = () => {
    if (displayGuest) {
      setContactForm({
        email: displayGuest.email || '',
        phone: displayGuest.phone || '',
        birthday: displayGuest.birthday ? displayGuest.birthday.split('T')[0] : '',
        anniversary: displayGuest.anniversary ? displayGuest.anniversary.split('T')[0] : '',
      });
    }
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return '-';
    }
  };

  // Check if a date (birthday/anniversary) is upcoming within 30 days
  const isUpcoming = (dateStr?: string) => {
    if (!dateStr) return false;
    try {
      const date = new Date(dateStr);
      const today = new Date();
      // Get this year's occurrence of the date
      const thisYear = new Date(today.getFullYear(), date.getMonth(), date.getDate());
      // If it's already passed this year, check next year
      const nextOccurrence = thisYear < today
        ? new Date(today.getFullYear() + 1, date.getMonth(), date.getDate())
        : thisYear;
      const daysUntil = Math.ceil((nextOccurrence.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntil >= 0 && daysUntil <= 30;
    } catch {
      return false;
    }
  };

  const handleCreateGuest = (e: React.FormEvent) => {
    e.preventDefault();
    const data: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
    };
    if (formData.email) data.email = formData.email;
    if (formData.phone) data.phone = formData.phone;
    if (formData.notes) data.notes = formData.notes;
    if (formData.allergies) data.allergies = formData.allergies.split(',').map(s => s.trim());
    if (formData.dietaryRestrictions) data.dietaryRestrictions = formData.dietaryRestrictions.split(',').map(s => s.trim());
    data.isVIP = formData.isVIP;
    createMutation.mutate(data);
  };

  const handleAddNote = () => {
    if (!selectedGuest || !newNote.trim()) return;
    addNoteMutation.mutate({ id: selectedGuest.id, note: newNote.trim() });
  };

  const handleToggleVip = () => {
    if (!selectedGuest) return;
    const currentDetails = guestDetails || selectedGuest;
    toggleVipMutation.mutate({ id: selectedGuest.id, isVIP: !currentDetails.isVIP });
  };

  // Use detailed data when available
  const displayGuest = guestDetails || selectedGuest;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Guests</h1>
            <p className="text-slate-500">View and manage guest profiles</p>
          </div>
          <HelpButton featureId="guests" />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refreshAllTagsMutation.mutate()}
            disabled={refreshAllTagsMutation.isPending}
            className="btn btn-secondary flex items-center gap-2"
            title="Recompute auto-tags for all guests"
          >
            <RefreshCw className={clsx('w-4 h-4', refreshAllTagsMutation.isPending && 'animate-spin')} />
            {refreshAllTagsMutation.isPending ? 'Refreshing...' : 'Refresh Tags'}
          </button>
          <button
            onClick={() => setShowTagManager(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Tag Glossary
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Guest
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            className="input w-full pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className={clsx(
              'input pl-9 pr-8 appearance-none cursor-pointer min-w-[160px]',
              tagFilter && 'bg-primary-50 border-primary-300 text-primary-700'
            )}
          >
            <option value="">All Tags</option>
            <optgroup label="Status">
              <option value="First-Time">First-Time</option>
              <option value="Returning">Returning</option>
              <option value="Regular">Regular</option>
              <option value="Loyal">Loyal</option>
              <option value="VIP">VIP</option>
              <option value="Lapsed">Lapsed</option>
            </optgroup>
            <optgroup label="Behavior">
              <option value="High Spender">High Spender</option>
              <option value="Wine Drinker">Wine Drinker</option>
              <option value="Large Party">Large Party</option>
              <option value="Weekend Diner">Weekend Diner</option>
              <option value="Booker">Booker</option>
            </optgroup>
            <optgroup label="Preferences">
              <option value="Window Seat">Window Seat</option>
              <option value="Booth">Booth</option>
              <option value="Quiet Table">Quiet Table</option>
              <option value="Vegetarian">Vegetarian</option>
              <option value="Gluten-Free">Gluten-Free</option>
            </optgroup>
            <optgroup label="Risk">
              <option value="High No-Show">High No-Show</option>
              <option value="Service Alert">Service Alert</option>
            </optgroup>
          </select>
        </div>
        <button
          onClick={() => setShowVipOnly(!showVipOnly)}
          className={clsx(
            'px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
            showVipOnly
              ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          )}
        >
          <Star className={clsx('w-4 h-4', showVipOnly && 'fill-amber-500')} />
          VIP Only
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{guests.length}</p>
              <p className="text-sm text-slate-500">Total Guests</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {guests.filter((g: Guest) => g.isVIP).length}
              </p>
              <p className="text-sm text-slate-500">VIP Guests</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {guests.filter((g: Guest) => g.totalVisits >= 5).length}
              </p>
              <p className="text-sm text-slate-500">Regulars (5+ visits)</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {guests.filter((g: Guest) => g.allergies && g.allergies.length > 0).length}
              </p>
              <p className="text-sm text-slate-500">With Allergies</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Celebrations */}
      {upcomingCelebrations.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-pink-50 to-purple-50 border-b border-pink-100">
            {reminderSuccess && (
              <div className="mb-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {reminderSuccess}
              </div>
            )}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-pink-500" />
                Upcoming Celebrations
                <span className="text-xs px-2 py-0.5 bg-pink-100 text-pink-700 rounded-full">
                  {upcomingCelebrations.length}
                </span>
              </h3>
              <span className="text-xs text-slate-500">Next 30 days</span>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {upcomingCelebrations.slice(0, 5).map((celebration: any) => (
              <div
                key={`${celebration.guestId}-${celebration.eventType}`}
                className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                onClick={() => {
                  const guest = guests.find((g: Guest) => g.id === celebration.guestId);
                  if (guest) setSelectedGuest(guest);
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {celebration.eventType === 'birthday' ? '🎂' : '✨'}
                  </span>
                  <div>
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      {celebration.guestName}
                      {celebration.isVIP && (
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                      )}
                    </p>
                    <p className="text-sm text-slate-500">
                      {celebration.eventType === 'birthday' ? 'Birthday' : 'Anniversary'} on{' '}
                      {new Date(celebration.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={clsx(
                    'text-xs px-2 py-1 rounded-full',
                    celebration.daysUntil === 0
                      ? 'bg-red-100 text-red-700 font-medium'
                      : celebration.daysUntil <= 7
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-slate-100 text-slate-600'
                  )}>
                    {celebration.daysUntil === 0
                      ? 'Today!'
                      : celebration.daysUntil === 1
                      ? 'Tomorrow'
                      : `${celebration.daysUntil} days`}
                  </span>
                  {celebration.reminderSent ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Reset this reminder to allow sending again?')) {
                          resetReminderMutation.mutate({
                            guestId: celebration.guestId,
                            occasion: celebration.eventType,
                            celebrationDate: celebration.date,
                          });
                        }
                      }}
                      className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full hover:bg-green-100 transition-colors"
                      title="Click to reset and send again"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Sent
                    </button>
                  ) : celebration.email && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const reminderId = `${celebration.guestId}-${celebration.eventType}`;
                        if (sendingReminderId === reminderId) return;
                        setSendingReminderId(reminderId);
                        sendReminderMutation.mutate({
                          guestId: celebration.guestId,
                          occasion: celebration.eventType,
                        });
                      }}
                      disabled={sendingReminderId === `${celebration.guestId}-${celebration.eventType}`}
                      className="p-1.5 rounded-lg hover:bg-pink-100 text-pink-600 disabled:opacity-50"
                      title="Send reservation invite"
                    >
                      {sendingReminderId === `${celebration.guestId}-${celebration.eventType}` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {upcomingCelebrations.length > 5 && (
              <div className="px-4 py-2 text-center">
                <button className="text-sm text-primary-600 hover:text-primary-700">
                  View all {upcomingCelebrations.length} upcoming celebrations
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Guest List */}
      {isLoading ? (
        <div className="text-center py-12">Loading guests...</div>
      ) : guests.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No guests found</p>
          {searchQuery && <p className="text-sm mt-1">Try a different search term</p>}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Guest</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 hidden md:table-cell">Contact</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Visits</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700 hidden sm:table-cell">Total Spent</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700 hidden lg:table-cell">Last Visit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {guests.map((guest: Guest) => (
                <tr
                  key={guest.id}
                  onClick={() => {
                    setSelectedGuest(guest);
                    setActiveTab('overview');
                  }}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {guest.firstName?.[0]}{guest.lastName?.[0]}
                          </span>
                        </div>
                        {guest.isVIP && (
                          <Star className="absolute -top-1 -right-1 w-4 h-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {guest.firstName} {guest.lastName}
                        </p>
                        {guest.allergies && guest.allergies.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-red-600">
                            <AlertTriangle className="w-3 h-3" />
                            {guest.allergies.slice(0, 2).join(', ')}
                            {guest.allergies.length > 2 && ` +${guest.allergies.length - 2}`}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-sm text-slate-600">
                      {guest.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {guest.phone}
                        </div>
                      )}
                      {guest.email && (
                        <div className="flex items-center gap-1 truncate max-w-[200px]">
                          <Mail className="w-3 h-3" />
                          {guest.email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={clsx(
                      'inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm',
                      guest.totalVisits >= 10 ? 'bg-emerald-100 text-emerald-700' :
                      guest.totalVisits >= 5 ? 'bg-primary-100 text-primary-700' :
                      'bg-slate-100 text-slate-700'
                    )}>
                      {guest.totalVisits}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell">
                    <span className="font-medium text-slate-900">
                      {formatCurrency(guest.totalSpendCents || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-slate-500 hidden lg:table-cell">
                    {formatDate(guest.lastVisitAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Guest Detail Modal */}
      {selectedGuest && displayGuest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-lg font-medium text-primary-700">
                      {displayGuest.firstName?.[0]}{displayGuest.lastName?.[0]}
                    </span>
                  </div>
                  {displayGuest.isVIP && (
                    <Star className="absolute -top-1 -right-1 w-5 h-5 text-amber-500 fill-amber-500" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    {displayGuest.firstName} {displayGuest.lastName}
                  </h2>
                  <p className="text-sm text-slate-500">
                    Guest since {formatDate(displayGuest.createdAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedGuest(null)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Tab Navigation — swipeable strip: five tabs don't fit a phone,
                and without horizontal scroll the far ones were unreachable. */}
            <div className="flex border-b border-slate-200 px-4 sm:px-6 overflow-x-auto scrollbar-hide">
              {[
                { id: 'overview' as const, label: 'Overview', icon: Users },
                { id: 'reservations' as const, label: 'Reservations', icon: CalendarDays },
                { id: 'orders' as const, label: 'Orders', icon: UtensilsCrossed },
                { id: 'servers' as const, label: 'Servers', icon: UserCheck },
                { id: 'insights' as const, label: 'Insights', icon: Sparkles },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0',
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <>
                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{displayGuest.totalVisits}</p>
                      <p className="text-xs text-slate-500">Total Visits</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(displayGuest.totalSpendCents || 0)}
                      </p>
                      <p className="text-xs text-slate-500">Total Spent</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(displayGuest.averageCheckSizeCents || 0)}
                      </p>
                      <p className="text-xs text-slate-500">Avg Check</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-900">Contact Information</h3>
                      {!editingContact ? (
                        <button
                          onClick={() => {
                            initContactForm();
                            setEditingContact(true);
                          }}
                          className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        >
                          <Settings2 className="w-3 h-3" />
                          Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingContact(false)}
                            className="text-xs text-slate-500 hover:text-slate-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              updateGuestMutation.mutate({
                                id: selectedGuest!.id,
                                data: {
                                  email: contactForm.email || null,
                                  phone: contactForm.phone || null,
                                  birthday: contactForm.birthday || null,
                                  anniversary: contactForm.anniversary || null,
                                },
                              });
                            }}
                            disabled={updateGuestMutation.isPending}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                          >
                            {updateGuestMutation.isPending ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3 h-3" />
                            )}
                            Save
                          </button>
                        </div>
                      )}
                    </div>

                    {editingContact ? (
                      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Email</label>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <input
                              type="email"
                              value={contactForm.email}
                              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                              placeholder="email@example.com"
                              className="input text-sm py-1.5 flex-1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Phone</label>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <input
                              type="tel"
                              value={contactForm.phone}
                              onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                              placeholder="+1-555-123-4567"
                              className="input text-sm py-1.5 flex-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                          <div>
                            <label className="text-xs text-slate-500 block mb-1">🎂 Birthday</label>
                            <input
                              type="date"
                              value={contactForm.birthday}
                              onChange={(e) => setContactForm({ ...contactForm, birthday: e.target.value })}
                              className="input text-sm py-1.5 w-full"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 block mb-1">✨ Anniversary</label>
                            <input
                              type="date"
                              value={contactForm.anniversary}
                              onChange={(e) => setContactForm({ ...contactForm, anniversary: e.target.value })}
                              className="input text-sm py-1.5 w-full"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                        {displayGuest.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span>{displayGuest.phone}</span>
                          </div>
                        )}
                        {displayGuest.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span>{displayGuest.email}</span>
                          </div>
                        )}
                        {!displayGuest.phone && !displayGuest.email && (
                          <p className="text-sm text-slate-400">No contact info on file</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Special Dates - Birthday & Anniversary */}
                  {(displayGuest.birthday || displayGuest.anniversary) && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <PartyPopper className="w-4 h-4" />
                        Special Dates
                      </h3>
                      <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 space-y-3">
                        {displayGuest.birthday && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">🎂</span>
                              <div>
                                <p className="text-sm font-medium text-slate-700">Birthday</p>
                                <p className="text-sm text-slate-600">{formatDate(displayGuest.birthday)}</p>
                              </div>
                            </div>
                            {isUpcoming(displayGuest.birthday) && (
                              <span className="text-xs px-2 py-1 bg-pink-100 text-pink-700 rounded-full">
                                Coming up!
                              </span>
                            )}
                          </div>
                        )}
                        {displayGuest.anniversary && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">✨</span>
                              <div>
                                <p className="text-sm font-medium text-slate-700">Anniversary</p>
                                <p className="text-sm text-slate-600">{formatDate(displayGuest.anniversary)}</p>
                              </div>
                            </div>
                            {isUpcoming(displayGuest.anniversary) && (
                              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                Coming up!
                              </span>
                            )}
                          </div>
                        )}
                        {/* Send reservation suggestion if contact info available and event upcoming */}
                        {(displayGuest.email || displayGuest.phone) &&
                         (isUpcoming(displayGuest.birthday) || isUpcoming(displayGuest.anniversary)) && (
                          <div className="pt-2 border-t border-pink-100">
                            <button
                              onClick={() => {
                                const eventType = isUpcoming(displayGuest.birthday) ? 'birthday' : 'anniversary';
                                // TODO: Integrate with email/SMS service
                                alert(`Send ${eventType} reservation suggestion to ${displayGuest.email || displayGuest.phone}?\n\nThis feature will be integrated with your email/SMS service.`);
                              }}
                              className="w-full text-sm px-3 py-2 bg-white border border-pink-200 rounded-lg text-pink-700 hover:bg-pink-50 flex items-center justify-center gap-2"
                            >
                              <Mail className="w-4 h-4" />
                              Send Reservation Suggestion
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Allergies & Dietary */}
                  {((displayGuest.allergies && displayGuest.allergies.length > 0) ||
                    (displayGuest.dietaryRestrictions && displayGuest.dietaryRestrictions.length > 0)) && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Dietary Information</h3>
                      <div className="space-y-2">
                        {displayGuest.allergies && displayGuest.allergies.length > 0 && (
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-red-700">Allergies</p>
                              <p className="text-sm text-slate-600">{displayGuest.allergies.join(', ')}</p>
                            </div>
                          </div>
                        )}
                        {displayGuest.dietaryRestrictions && displayGuest.dietaryRestrictions.length > 0 && (
                          <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-amber-500 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-700">Dietary Restrictions</p>
                              <p className="text-sm text-slate-600">{displayGuest.dietaryRestrictions.join(', ')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Guest Tags */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Tags
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => computeTagsMutation.mutate(selectedGuest!.id)}
                          disabled={computeTagsMutation.isPending}
                          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                          title="Recompute auto-generated tags"
                        >
                          <RefreshCw className={clsx('w-3 h-3', computeTagsMutation.isPending && 'animate-spin')} />
                          Refresh
                        </button>
                        <button
                          onClick={() => setShowAddTagDropdown(!showAddTagDropdown)}
                          className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add Tag
                        </button>
                      </div>
                    </div>

                    {/* Add Tag Dropdown */}
                    {showAddTagDropdown && (
                      <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="space-y-2 mb-2">
                          <div className="flex gap-2">
                            <select
                              value={selectedTagLayer}
                              onChange={(e) => setSelectedTagLayer(e.target.value)}
                              className="input text-sm py-1.5 px-2 w-32"
                            >
                              <option value="preference">Preference</option>
                              <option value="relationship">Relationship</option>
                              <option value="risk">Risk</option>
                            </select>
                            <span className="text-xs text-slate-400 self-center">Layer</span>
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Enter custom tag name..."
                              className="input flex-1 text-sm py-1.5 min-w-0"
                              value={newTagInput}
                              onChange={(e) => setNewTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newTagInput.trim()) {
                                  addTagMutation.mutate({
                                    id: selectedGuest!.id,
                                    tag: newTagInput.trim(),
                                    layer: selectedTagLayer,
                                  });
                                }
                              }}
                            />
                            <button
                              onClick={() => {
                                if (newTagInput.trim()) {
                                  addTagMutation.mutate({
                                    id: selectedGuest!.id,
                                    tag: newTagInput.trim(),
                                    layer: selectedTagLayer,
                                  });
                                }
                              }}
                              disabled={!newTagInput.trim() || addTagMutation.isPending}
                              className="btn btn-primary text-sm py-1.5 px-4"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {selectedTagLayer === 'preference' && (
                            <>
                              {['Window Seat', 'Booth', 'Bar Seat', 'Quiet Table', 'Wine Pairing', 'Early Bird', 'Late Night'].map(tag => (
                                <button
                                  key={tag}
                                  onClick={() => addTagMutation.mutate({ id: selectedGuest!.id, tag, layer: 'preference' })}
                                  className="text-xs px-2 py-1 bg-white border border-slate-200 rounded hover:bg-slate-100"
                                >
                                  {tag}
                                </button>
                              ))}
                            </>
                          )}
                          {selectedTagLayer === 'relationship' && (
                            <>
                              {['Business Diner', 'Date Night', 'Family', 'Group Organizer', 'Industry'].map(tag => (
                                <button
                                  key={tag}
                                  onClick={() => addTagMutation.mutate({ id: selectedGuest!.id, tag, layer: 'relationship' })}
                                  className="text-xs px-2 py-1 bg-white border border-slate-200 rounded hover:bg-slate-100"
                                >
                                  {tag}
                                </button>
                              ))}
                            </>
                          )}
                          {selectedTagLayer === 'risk' && (
                            <>
                              {['Payment Issue', 'Incident', 'Service Alert'].map(tag => (
                                <button
                                  key={tag}
                                  onClick={() => addTagMutation.mutate({ id: selectedGuest!.id, tag, layer: 'risk' })}
                                  className="text-xs px-2 py-1 bg-white border border-slate-200 rounded hover:bg-slate-100"
                                >
                                  {tag}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tags Display */}
                    {tagsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading tags...
                      </div>
                    ) : guestTags?.tagsByLayer ? (
                      <div className="space-y-3">
                        {/* Status Tags */}
                        {guestTags.tagsByLayer.status?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                              <UserCircle className="w-3.5 h-3.5" />
                              Status
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {guestTags.tagsByLayer.status.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="group inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs"
                                  title={tag.source === 'auto' ? 'Auto-generated (click X to hide)' : tag.source === 'ai' ? 'Added via AI' : 'Manually added'}
                                >
                                  {tag.tag}
                                  {tag.source === 'auto' && <span className="text-blue-400 text-[10px]">auto</span>}
                                  <button
                                    onClick={() => removeTagMutation.mutate({ guestId: selectedGuest!.id, tagId: tag.id })}
                                    className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                                    title={tag.source === 'auto' ? 'Hide this auto-tag' : 'Remove tag'}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Behavior Tags */}
                        {guestTags.tagsByLayer.behavior?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                              <BarChart3 className="w-3.5 h-3.5" />
                              Behavior
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {guestTags.tagsByLayer.behavior.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="group inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs"
                                  title={tag.source === 'auto' ? 'Auto-generated (click X to hide)' : tag.source === 'ai' ? 'Added via AI' : 'Manually added'}
                                >
                                  {tag.tag}
                                  {tag.source === 'auto' && <span className="text-emerald-400 text-[10px]">auto</span>}
                                  <button
                                    onClick={() => removeTagMutation.mutate({ guestId: selectedGuest!.id, tagId: tag.id })}
                                    className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                                    title={tag.source === 'auto' ? 'Hide this auto-tag' : 'Remove tag'}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Preference Tags */}
                        {guestTags.tagsByLayer.preference?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                              <Settings2 className="w-3.5 h-3.5" />
                              Preferences
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {guestTags.tagsByLayer.preference.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="group inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs"
                                >
                                  {tag.tag}
                                  {tag.source === 'auto' && <span className="text-purple-400 text-[10px]">auto</span>}
                                  <button
                                    onClick={() => removeTagMutation.mutate({ guestId: selectedGuest!.id, tagId: tag.id })}
                                    className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Relationship Tags */}
                        {guestTags.tagsByLayer.relationship?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                              <Link2 className="w-3.5 h-3.5" />
                              Relationship
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {guestTags.tagsByLayer.relationship.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="group inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-full text-xs"
                                >
                                  {tag.tag}
                                  {tag.source === 'auto' && <span className="text-amber-400 text-[10px]">auto</span>}
                                  <button
                                    onClick={() => removeTagMutation.mutate({ guestId: selectedGuest!.id, tagId: tag.id })}
                                    className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Risk Tags */}
                        {guestTags.tagsByLayer.risk?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1.5 text-xs font-medium text-red-500 mb-1.5">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Risk Alerts
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {guestTags.tagsByLayer.risk.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="group inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs"
                                  title={tag.notes || undefined}
                                >
                                  {tag.tag}
                                  {tag.source === 'auto' && <span className="text-red-400 text-[10px]">auto</span>}
                                  <button
                                    onClick={() => removeTagMutation.mutate({ guestId: selectedGuest!.id, tagId: tag.id })}
                                    className="opacity-0 group-hover:opacity-100 hover:text-red-900 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No tags message */}
                        {!guestTags.tagsByLayer.status?.length &&
                          !guestTags.tagsByLayer.behavior?.length &&
                          !guestTags.tagsByLayer.preference?.length &&
                          !guestTags.tagsByLayer.relationship?.length &&
                          !guestTags.tagsByLayer.risk?.length && (
                          <p className="text-sm text-slate-400">No tags yet. Add tags or close a check to generate auto-tags.</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No tags available</p>
                    )}
                  </div>

                  {/* Recent Visits */}
                  {displayGuest.recentChecks && displayGuest.recentChecks.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Recent Visits</h3>
                      <div className="space-y-2">
                        {displayGuest.recentChecks.slice(0, 5).map((check: any) => (
                          <div key={check.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span>{formatDate(check.openedAt)}</span>
                              {check.tableName && (
                                <span className="text-slate-400">• {check.tableName}</span>
                              )}
                            </div>
                            <span className="font-medium text-slate-900">
                              {formatCurrency(check.totalCents || 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Notes</h3>
                    {displayGuest.notes ? (
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 whitespace-pre-wrap">
                        {displayGuest.notes}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No notes yet</p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <input
                        type="text"
                        placeholder="Add a note..."
                        className="input flex-1"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                      />
                      <button
                        onClick={handleAddNote}
                        disabled={!newNote.trim() || addNoteMutation.isPending}
                        className="btn btn-primary"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Reservations Tab */}
              {activeTab === 'reservations' && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Reservation History</h3>
                  {displayGuest.reservationHistory && displayGuest.reservationHistory.length > 0 ? (
                    <div className="space-y-3">
                      {displayGuest.reservationHistory.map((res: any) => {
                        const isPast = new Date(res.date) < new Date();
                        const statusColors: Record<string, string> = {
                          confirmed: 'bg-emerald-100 text-emerald-700',
                          seated: 'bg-blue-100 text-blue-700',
                          completed: 'bg-slate-100 text-slate-700',
                          cancelled: 'bg-red-100 text-red-700',
                          'no-show': 'bg-red-100 text-red-700',
                        };
                        return (
                          <div
                            key={res.id}
                            className={clsx(
                              'border rounded-lg p-4',
                              res.status === 'no-show' ? 'border-red-200 bg-red-50' : 'border-slate-200'
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className={clsx(
                                  'w-10 h-10 rounded-lg flex items-center justify-center',
                                  isPast ? 'bg-slate-100' : 'bg-primary-100'
                                )}>
                                  <CalendarDays className={clsx(
                                    'w-5 h-5',
                                    isPast ? 'text-slate-500' : 'text-primary-600'
                                  )} />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {format(new Date(res.date), 'MMM d, yyyy')} at {res.time}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3.5 h-3.5" />
                                      Party of {res.partySize}
                                    </span>
                                    {res.occasion && (
                                      <span className="flex items-center gap-1">
                                        <PartyPopper className="w-3.5 h-3.5" />
                                        {res.occasion}
                                      </span>
                                    )}
                                  </div>
                                  {res.specialRequests && (
                                    <p className="mt-2 text-sm text-slate-600 italic">
                                      "{res.specialRequests}"
                                    </p>
                                  )}
                                  {res.notes && (
                                    <p className="mt-2 text-sm text-slate-500 bg-slate-50 rounded px-2 py-1">
                                      📝 {res.notes}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className={clsx(
                                'px-2.5 py-1 rounded-full text-xs font-medium capitalize',
                                statusColors[res.status] || 'bg-slate-100 text-slate-700'
                              )}>
                                {res.status === 'no-show' ? (
                                  <span className="flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    No Show
                                  </span>
                                ) : res.status}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No reservation history</p>
                    </div>
                  )}
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Order History</h3>
                  {displayGuest.preferences?.itemHistory && displayGuest.preferences.itemHistory.length > 0 ? (
                    <div className="space-y-2">
                      {[...displayGuest.preferences.itemHistory]
                        .sort((a, b) => b.orderCount - a.orderCount)
                        .map((item, idx) => {
                          const sentimentStyles: Record<string, { bg: string; icon: React.ReactNode }> = {
                            loved: { bg: 'bg-pink-50 border-pink-200', icon: <Heart className="w-4 h-4 text-pink-500 fill-pink-500" /> },
                            liked: { bg: 'bg-emerald-50 border-emerald-200', icon: <CheckCircle className="w-4 h-4 text-emerald-500" /> },
                            neutral: { bg: 'bg-slate-50 border-slate-200', icon: null },
                            disliked: { bg: 'bg-slate-50 border-slate-200', icon: <ThumbsDown className="w-4 h-4 text-slate-400" /> },
                            wont_order: { bg: 'bg-red-50 border-red-200', icon: <XCircle className="w-4 h-4 text-red-500" /> },
                          };
                          const style = sentimentStyles[item.sentiment] || sentimentStyles.neutral;
                          return (
                            <div
                              key={idx}
                              className={clsx(
                                'flex items-center justify-between p-3 rounded-lg border',
                                style.bg
                              )}
                            >
                              <div className="flex items-center gap-3">
                                {style.icon && <span>{style.icon}</span>}
                                <div>
                                  <p className="font-medium text-slate-900">{item.menuItemName}</p>
                                  <p className="text-xs text-slate-500">
                                    Last ordered: {formatDate(item.lastOrderedAt)}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-slate-900">{item.orderCount}x</p>
                                <p className="text-xs text-slate-500">ordered</p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No order history recorded</p>
                    </div>
                  )}
                </div>
              )}

              {/* Servers Tab */}
              {activeTab === 'servers' && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Server History</h3>
                  {displayGuest.serverHistory && displayGuest.serverHistory.length > 0 ? (
                    <div className="space-y-3">
                      {[...displayGuest.serverHistory]
                        .sort((a, b) => b.visitCount - a.visitCount)
                        .map((server, idx) => {
                          const isTopServer = idx === 0;
                          return (
                            <div
                              key={server.serverId || idx}
                              className={clsx(
                                'flex items-center justify-between p-4 rounded-lg border',
                                isTopServer ? 'border-primary-200 bg-primary-50' : 'border-slate-200'
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div className={clsx(
                                  'w-10 h-10 rounded-full flex items-center justify-center',
                                  isTopServer ? 'bg-primary-100' : 'bg-slate-100'
                                )}>
                                  <UserCheck className={clsx(
                                    'w-5 h-5',
                                    isTopServer ? 'text-primary-600' : 'text-slate-500'
                                  )} />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {server.serverName || 'Unknown Server'}
                                    {isTopServer && (
                                      <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                                        Most Frequent
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    Last served: {formatDate(server.lastServed)}
                                  </p>
                                  {server.tablesServed && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      Tables: {server.tablesServed}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-slate-900">{server.visitCount}</p>
                                <p className="text-xs text-slate-500">visits</p>
                                <p className="text-sm font-medium text-emerald-600 mt-1">
                                  {formatCurrency(server.totalSpentCents)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No server history available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Insights Tab */}
              {activeTab === 'insights' && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    <h3 className="font-semibold text-slate-900">AI Experience Analysis</h3>
                    {insights?.aiPowered && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        AI Powered
                      </span>
                    )}
                  </div>

                  {insightsLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-3" />
                      <p className="text-slate-500">Analyzing guest experience...</p>
                    </div>
                  ) : insights ? (
                    <div className="space-y-4">
                      {/* Visit Summary */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Visit Summary</h4>
                        <p className="text-sm text-slate-600">{insights.visitSummary}</p>
                      </div>

                      {/* Preferences */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-emerald-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                            <Heart className="w-4 h-4" />
                            Loves
                          </h4>
                          <p className="text-sm text-emerald-600">{insights.preferences?.loves || 'No data'}</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                            <ThumbsDown className="w-4 h-4" />
                            Avoids
                          </h4>
                          <p className="text-sm text-red-600">{insights.preferences?.avoids || 'No data'}</p>
                        </div>
                      </div>

                      {/* Service Notes */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-700 mb-2">Service Notes</h4>
                        <p className="text-sm text-blue-600">{insights.serviceNotes}</p>
                      </div>

                      {/* Recommendations */}
                      {insights.recommendations && insights.recommendations.length > 0 && (
                        <div className="bg-purple-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-purple-700 mb-2">Recommendations</h4>
                          <ul className="space-y-1">
                            {insights.recommendations.map((rec, idx) => (
                              <li key={idx} className="text-sm text-purple-600 flex items-start gap-2">
                                <span className="text-purple-400">•</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Highlights */}
                      {insights.highlights && insights.highlights.length > 0 && (
                        <div className="bg-amber-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-amber-700 mb-2">Key Highlights for Staff</h4>
                          <div className="flex flex-wrap gap-2">
                            {insights.highlights.map((highlight, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm"
                              >
                                {highlight}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Unable to load insights</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <button
                onClick={handleToggleVip}
                disabled={toggleVipMutation.isPending}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                  displayGuest.isVIP
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                <Star className={clsx('w-4 h-4', displayGuest.isVIP && 'fill-amber-500')} />
                {displayGuest.isVIP ? 'VIP Guest' : 'Mark as VIP'}
              </button>
              <button
                onClick={() => setSelectedGuest(null)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Guest Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Add Guest</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={handleCreateGuest}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    className="input w-full"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="input w-full"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Allergies <span className="text-slate-400 font-normal">(comma separated)</span>
                  </label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="e.g. Peanuts, Shellfish"
                    value={formData.allergies}
                    onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Dietary Restrictions <span className="text-slate-400 font-normal">(comma separated)</span>
                  </label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="e.g. Vegetarian, Gluten-free"
                    value={formData.dietaryRestrictions}
                    onChange={(e) => setFormData({ ...formData, dietaryRestrictions: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    className="input w-full"
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isVIP"
                    checked={formData.isVIP}
                    onChange={(e) => setFormData({ ...formData, isVIP: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <label htmlFor="isVIP" className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-500" />
                    VIP Guest
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Adding...' : 'Add Guest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Manager Modal */}
      {showTagManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Tag Glossary</h2>
                <p className="text-sm text-slate-500">View all available tags and create custom ones</p>
              </div>
              <button
                onClick={() => setShowTagManager(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Create Custom Tag */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Custom Tag
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    placeholder="Tag name"
                    className="input"
                    value={newCustomTag.name}
                    onChange={(e) => setNewCustomTag({ ...newCustomTag, name: e.target.value })}
                  />
                  <select
                    value={newCustomTag.layer}
                    onChange={(e) => setNewCustomTag({ ...newCustomTag, layer: e.target.value })}
                    className="input"
                  >
                    <option value="preference">Preference</option>
                    <option value="relationship">Relationship</option>
                    <option value="risk">Risk</option>
                    <option value="status">Status</option>
                    <option value="behavior">Behavior</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    className="input"
                    value={newCustomTag.description}
                    onChange={(e) => setNewCustomTag({ ...newCustomTag, description: e.target.value })}
                  />
                  <button
                    onClick={() => {
                      if (newCustomTag.name.trim()) {
                        createTagDefMutation.mutate({
                          name: newCustomTag.name.trim(),
                          layer: newCustomTag.layer,
                          description: newCustomTag.description || undefined,
                        });
                      }
                    }}
                    disabled={!newCustomTag.name.trim() || createTagDefMutation.isPending}
                    className="btn btn-primary"
                  >
                    {createTagDefMutation.isPending ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>

              {/* Tag Glossary by Layer */}
              {tagDefinitions?.systemDefaults && (
                <div className="space-y-6">
                  {/* Status Tags */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-blue-600" />
                      Status Tags
                      <span className="text-xs text-slate-400 font-normal">(Who they are)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tagDefinitions.systemDefaults.status.map((tag) => (
                        <div key={tag.name} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium whitespace-nowrap">
                            {tag.name}
                          </span>
                          <span className="text-sm text-slate-600">{tag.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Behavior Tags */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-emerald-600" />
                      Behavior Tags
                      <span className="text-xs text-slate-400 font-normal">(What they do)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tagDefinitions.systemDefaults.behavior.map((tag) => (
                        <div key={tag.name} className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium whitespace-nowrap">
                            {tag.name}
                          </span>
                          <span className="text-sm text-slate-600">{tag.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preference Tags */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Settings2 className="w-4 h-4 text-purple-600" />
                      Preference Tags
                      <span className="text-xs text-slate-400 font-normal">(How they like it)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tagDefinitions.systemDefaults.preference.map((tag) => (
                        <div key={tag.name} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium whitespace-nowrap">
                            {tag.name}
                          </span>
                          <span className="text-sm text-slate-600">{tag.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Relationship Tags */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-amber-600" />
                      Relationship Tags
                      <span className="text-xs text-slate-400 font-normal">(Who they're with)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tagDefinitions.systemDefaults.relationship.map((tag) => (
                        <div key={tag.name} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm font-medium whitespace-nowrap">
                            {tag.name}
                          </span>
                          <span className="text-sm text-slate-600">{tag.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Risk Tags */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      Risk Tags
                      <span className="text-xs text-slate-400 font-normal">(Operational concerns)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tagDefinitions.systemDefaults.risk.map((tag) => (
                        <div key={tag.name} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium whitespace-nowrap">
                            {tag.name}
                          </span>
                          <span className="text-sm text-slate-600">{tag.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom Tags */}
                  {tagDefinitions.definitions && tagDefinitions.definitions.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Tag className="w-4 h-4 text-slate-600" />
                        Custom Tags
                        <span className="text-xs text-slate-400 font-normal">(Your restaurant's tags)</span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {tagDefinitions.definitions.filter(d => !d.isSystem).map((tag) => (
                          <div key={tag.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <span className="px-2 py-1 bg-slate-200 text-slate-700 rounded text-sm font-medium whitespace-nowrap">
                              {tag.name}
                            </span>
                            <span className="text-sm text-slate-600 flex-1">{tag.description || 'No description'}</span>
                            <span className="text-xs text-slate-400 capitalize">{tag.layer}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setShowTagManager(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
